import {
  Alert,
  schAlertSeverity,
  schAlertStatus,
  schAlertType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

const TIP = 'Comp condition ratings should be within ±1 level of the Subject.';
const TARGET_CELL_TOOLTIP = 'Exceeds ±1 level from Subject';

export const inconsistentConditionRating: AlertDefinition = ({
  compSequence,
  compType,
  order,
  propertyDataEntities,
  fullCompSequence,
}) => {
  if (order.hasArvOption && !compType) return [];

  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;
  if (!subjectData) return [];

  const scopedSubjectData = order.hasArvOption
    ? getSubjectDataForCompType(subjectData, compType)
    : subjectData;
  const subjectCondition = scopedSubjectData.improvements?.uadCondition;
  if (subjectCondition === undefined) return [];

  return compSequence
    .map((compID, index) => {
      const compData = propertyDataEntities[compID] as CompPropertyDataState | undefined;
      const compCondition = compData?.compData.editableData.improvements?.uadCondition;
      if (compCondition === undefined || Math.abs(compCondition - subjectCondition) <= 1) {
        return undefined;
      }

      const compIndex = fullCompSequence?.indexOf(compID);
      const compLabel = compIndex !== undefined ? compIndex + 1 : index + 1;
      const targetCellKey = `${compID}-editableData.improvements.uadCondition`;

      return {
        type: order.hasArvOption
          ? getScopedAlertType(schAlertType.enum.inconsistentConditionRating, compType)
          : schAlertType.enum.inconsistentConditionRating,
        status: schAlertStatus.enum.active,
        severity: schAlertSeverity.enum.warning,
        orderType: ['appraisify'],
        ...(order.hasArvOption ? { compType } : {}),
        message: `Comp ${compLabel} does not align with the Subject's condition rating`,
        propertyID: compID,
        triggers: getAlertTriggers({
          fixRoute: schPairedCostRouteItem.enum.features,
          order,
          includeUpdate: true,
        }),
        fixingInfo: {
          tips: TIP,
          pairedCostRouteItem: schPairedCostRouteItem.enum.features,
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey,
          targetCellKeys: [targetCellKey],
          targetCellTooltip: TARGET_CELL_TOOLTIP,
        },
      } as Alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);
};
